import React, { useCallback, useEffect, useState } from "react";
import { getAuditLog, getAuditLogExportUrl, getAuditLogStats } from "../../../api/globalAdminApi";
import { EnvBadge, Modal, Notice, PageHeader, Panel, SideDrawer, StatePanel, StatTiles, StatusChip } from "../ui/AdminUi";
import { I } from "../ui/icons";

const ENVIRONMENTS = [["all", "All envs"], ["local", "Local"], ["dev", "Dev"], ["staging", "Staging"], ["production", "Production"]];
const ACTIONS = [
  ["all", "All actions"], ["impersonation_start", "Impersonation"], ["impersonation_end", "Impersonation ended"],
  ["tenant_copy", "Tenant copy"], ["role_change", "Role change"], ["settings_change", "Settings"], ["data_export", "Data export"], ["login", "Login"],
];
const RANGES = [["1", "Last 24 hours"], ["7", "Last 7 days"], ["30", "Last 30 days"]];

/** Saved views that ship with the console. Admins cannot add their own yet. */
const PRESETS = {
  productionFailures: { label: "Production failures", filters: { environment: "production", status: "failure" } },
  impersonations: { label: "Impersonations", filters: { action: "impersonation_start" } },
  myActions: { label: "My actions", filters: { search: "__me__" } },
};

const DEFAULT_FILTERS = { search: "", action: "all", tenant: "all", environment: "all", status: "all", days: "7" };

const SELECT_STYLE = { width: "auto", minWidth: 150, height: 32 };

/**
 * Every sensitive action across training centers and environments. The summary tiles are filters; the table is
 * read-only; one event opens in a drawer with its details.
 */
export default function AuditLogView({ tenants, currentUserName }) {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [activePreset, setActivePreset] = useState(null);
  const [tile, setTile] = useState(null);
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [exporting, setExporting] = useState(false);

  const resolvedSearch = filters.search === "__me__" ? currentUserName || "" : filters.search;
  const query = { ...filters, search: resolvedSearch, days: Number(filters.days) };

  const fetchStats = useCallback(() => {
    getAuditLogStats(Number(filters.days)).then(setStats).catch(() => setStats(null));
  }, [filters.days]);

  const fetchLog = useCallback(() => {
    setLoading(true);
    setError(null);
    getAuditLog({ ...query, page: 0, size: 50 })
      .then((r) => { setEntries(r.entries || []); setTotal(r.totalCount || 0); })
      .catch((err) => { setEntries([]); setTotal(0); setError(err?.message || "The audit log could not be loaded."); })
      .finally(() => setLoading(false));
    // query is derived from filters and currentUserName; those are the real dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentUserName]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchLog(); }, [fetchLog]);

  const setFilter = (key, value) => {
    setActivePreset(null);
    setFilters((f) => ({ ...f, [key]: value }));
  };

  const applyPreset = (key) => {
    if (activePreset === key) {
      setActivePreset(null);
      setFilters(DEFAULT_FILTERS);
      setTile(null);
      return;
    }
    setActivePreset(key);
    setTile(null);
    setFilters({ ...DEFAULT_FILTERS, ...PRESETS[key].filters });
  };

  const pickTile = (key) => {
    setActivePreset(null);
    setTile(key);
    if (key === "failed") setFilters({ ...DEFAULT_FILTERS, status: "failure", days: filters.days });
    else if (key === "copies") setFilters({ ...DEFAULT_FILTERS, action: "tenant_copy", days: filters.days });
    else setFilters({ ...DEFAULT_FILTERS, days: filters.days });
  };

  const clearFilters = () => { setActivePreset(null); setTile(null); setFilters(DEFAULT_FILTERS); };

  const exportUrl = getAuditLogExportUrl({ ...query, days: Number(filters.days) });
  const filtered = Boolean(filters.search || filters.action !== "all" || filters.environment !== "all" || filters.status !== "all" || filters.tenant !== "all");
  const rangeLabel = (RANGES.find(([v]) => v === filters.days) || RANGES[1])[1].toLowerCase();

  return (
    <>
      <PageHeader
        title="Audit log"
        desc="Every sensitive action across all training centers and environments, newest first."
        actions={<button type="button" className="b sec" disabled={loading || Boolean(error) || total === 0} onClick={() => setExporting(true)}><I.download s={14} />Export</button>}
      />

      <StatTiles
        active={tile}
        onPick={pickTile}
        items={[
          { label: `Total events · ${filters.days}d`, value: stats?.totalEvents ?? "—" },
          { key: "failed", icon: <I.stop s={13} />, label: "Failed actions", value: stats?.failedActions ?? "—", tone: stats?.failedActions > 0 ? "var(--st-bad)" : undefined },
          { key: "copies", label: "Tenant copies", value: stats?.copyCount ?? "—" },
          { label: "Active impersonations", value: stats?.activeImpersonations ?? "—", foot: stats?.activeImpersonations > 0 ? "Live now" : undefined },
        ]}
      />

      <Panel>
        <div className="panel-h">
          <label className="srch">
            <I.search />
            <input
              placeholder="Search actor, action, center or event" aria-label="Search audit log"
              value={filters.search === "__me__" ? "" : filters.search} onChange={(e) => setFilter("search", e.target.value)}
            />
          </label>
          <div className="seg" role="group" aria-label="Environment">
            {ENVIRONMENTS.map(([v, l]) => (
              <button key={v} type="button" className={filters.environment === v ? "on" : ""} aria-pressed={filters.environment === v} onClick={() => setFilter("environment", v)}>{l}</button>
            ))}
          </div>
          <select className="sel" style={SELECT_STYLE} aria-label="Action" value={filters.action} onChange={(e) => setFilter("action", e.target.value)}>
            {ACTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select className="sel" style={{ ...SELECT_STYLE, minWidth: 170 }} aria-label="Training center" value={filters.tenant} onChange={(e) => setFilter("tenant", e.target.value)}>
            <option value="all">All training centers</option>
            {tenants.map((t) => <option key={t.id} value={t.code || String(t.id)}>{t.name}</option>)}
          </select>
          <select className="sel" style={SELECT_STYLE} aria-label="Date range" value={filters.days} onChange={(e) => setFilter("days", e.target.value)}>
            {RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <span className="sp" />
          <div className="tb">
            <span className="eyebrow">Saved</span>
            {Object.entries(PRESETS).map(([key, preset]) => (
              <button key={key} type="button" className={`fchip${activePreset === key ? " on" : ""}`} aria-pressed={activePreset === key} onClick={() => applyPreset(key)}>
                <I.bookmark s={12} />{preset.label}
              </button>
            ))}
          </div>
        </div>

        {error ? (
          <StatePanel kind="error" what="audit events" error={error} onRetry={fetchLog} />
        ) : loading ? (
          <StatePanel kind="loading" what="audit events" />
        ) : entries.length === 0 ? (
          <StatePanel
            kind="empty"
            what={filtered ? "matching events" : "audit events"}
            why={filtered
              ? "No events match these filters. Clear them to see all events."
              : `No sensitive actions have been recorded in the last ${filters.days} days. Events appear here as soon as an admin impersonates, copies, publishes or changes a catalogue item.`}
            action={filtered ? <button type="button" className="b sec" onClick={clearFilters}>Clear filters</button> : null}
          />
        ) : (
          <div className="tw">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Time</th><th>Actor</th><th>Action</th><th>Training center</th><th>Env</th><th>Outcome</th><th />
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className={`click${open?.id === e.id ? " sel" : ""}`} tabIndex={0} onClick={() => setOpen(e)} onKeyDown={(ev) => ev.key === "Enter" && setOpen(e)}>
                    <td className="mono" style={{ fontSize: 12.5, whiteSpace: "nowrap" }}>{e.tsAbsolute || e.tsDate}</td>
                    <td>{e.actor?.name || "System"}</td>
                    <td>
                      <b style={{ fontWeight: 600 }}>{e.actionLabel || e.action}</b>
                      <div className="sub" style={{ fontSize: 12.5 }}>{e.description}</div>
                    </td>
                    <td>{e.tenantCode || "Platform"}</td>
                    <td>{e.environment ? <EnvBadge env={e.environment} label={e.environment.charAt(0).toUpperCase() + e.environment.slice(1)} /> : <span className="sub">—</span>}</td>
                    <td><StatusChip status={outcomeOf(e.status)} label={outcomeLabel(e.status)} /></td>
                    <td className="r sub"><I.chev /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && entries.length > 0 && (
          <div className="pfoot">
            <span>Showing {entries.length} of {total} events</span>
            <span style={{ marginLeft: "auto" }}>Retained 90 days</span>
          </div>
        )}
      </Panel>

      <SideDrawer
        open={Boolean(open)}
        onClose={() => setOpen(null)}
        title={open ? (open.actionLabel || open.action) : ""}
        sub={open ? (
          <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
            <StatusChip status={outcomeOf(open.status)} label={outcomeLabel(open.status)} />
            {open.environment && <EnvBadge env={open.environment} />}
          </div>
        ) : null}
        footer={open ? (
          <>
            <button type="button" className="b ghost" onClick={() => navigator.clipboard?.writeText(open.id)}><I.copy s={14} />Copy event ID</button>
            <button type="button" className="b pri" onClick={() => setOpen(null)}>Done</button>
          </>
        ) : null}
      >
        {open && <EventDetail event={open} />}
      </SideDrawer>

      <Modal
        open={exporting}
        title="Export audit events"
        labelId="audit-export-title"
        onClose={() => setExporting(false)}
        footer={(
          <>
            <button type="button" className="b ghost" onClick={() => setExporting(false)}>Cancel</button>
            <button type="button" className="b pri" onClick={() => { setExporting(false); window.open(exportUrl, "_blank", "noopener"); }}><I.download s={14} />Download CSV</button>
          </>
        )}
      >
        <p className="cons">The CSV will contain the events that match your current filters, for the {rangeLabel}{filtered ? "" : " (no other filters applied)"}.</p>
        <dl className="dl">
          <dt>Columns</dt><dd>Time, actor, role, action, description, training center, environment, status, IP address, session ID and server detail</dd>
          <dt>Not included</dt><dd>Request bodies and personal data of impersonated users</dd>
          <dt>Range</dt><dd style={{ textTransform: "capitalize" }}>{rangeLabel}</dd>
        </dl>
        <Notice kind="neutral">Exporting is itself recorded in this log as a data export.</Notice>
      </Modal>
    </>
  );
}

function outcomeOf(status) {
  if (status === "failure" || status === "failed" || status === "fail") return "Failed";
  if (status === "warning" || status === "warn") return "Warn";
  return "Success";
}

function outcomeLabel(status) {
  return { Failed: "Failed", Warn: "Warning", Success: "Success" }[outcomeOf(status)];
}

function EventDetail({ event }) {
  const outcome = outcomeOf(event.status);
  const rows = [
    ["Event ID", event.id, true],
    ["Time", event.tsAbsolute || event.tsDate],
    ["Actor", event.actor ? `${event.actor.name}${event.actor.role ? ` · ${event.actor.role}` : ""}` : "System"],
    ["Training center", event.tenantCode || "Platform"],
    ["Environment", event.environment || "—"],
    ["IP address", event.ip || "—", true],
    ["Session", event.sessionId || "—", true],
  ];
  return (
    <div style={{ display: "grid", gap: 16 }}>
      {event.description && <p className="cons sub" style={{ fontSize: 13.5 }}>{event.description}</p>}
      {outcome === "Failed" && event.meta && <Notice kind="bad"><b>Server detail</b><br />{event.meta}</Notice>}
      <dl className="dl">
        {rows.map(([label, value, mono]) => (
          <React.Fragment key={label}>
            <dt>{label}</dt>
            <dd className={mono ? "mono" : undefined}>{value}</dd>
          </React.Fragment>
        ))}
      </dl>
      {event.meta && outcome !== "Failed" && (
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Details</div>
          <div style={{ fontSize: 13, padding: "10px 13px", border: "1px solid var(--ga-line)", borderRadius: 8 }}>{event.meta}</div>
        </div>
      )}
    </div>
  );
}
